import React, { useCallback, useEffect, useRef, useState } from 'react';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import './ComposerEmojiButton.css';

/**
 * composer-v2 ComposerEmojiButton — 😊 trigger + emoji-mart Picker in
 * the composer-v2 bottom-actions row. KAP-307.
 *
 * Reuses `@emoji-mart/react` Picker props verbatim from
 * `ReactionPicker.js:81-91` (theme="auto", previewPosition="none",
 * skinTonePosition="none", navPosition="top", perLine=8, emojiSize=18,
 * maxFrequentRows=1) so reaction picker and composer picker feel
 * identical. The focus-on-mount + Esc handler mirrors
 * `ReactionPicker.js:24-35` (KAP-294 round 2 a11y pattern).
 *
 * The component is decoupled from Lexical — when the user selects an
 * emoji it calls `props.onSelectEmoji(glyph)`. The parent
 * (`MessageComposerV2`) bridges to Lexical via a `pendingEmoji` state
 * + an `EmojiInsertPlugin` inside `<LexicalComposer>`, mirroring how
 * `MentionInsertPlugin` works for @-mentions.
 */

function ComposerEmojiButton({ onSelectEmoji, disabled }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);

  const closeAndRestoreFocus = useCallback(() => {
    setOpen(false);
    window.setTimeout(() => {
      if (triggerRef.current) triggerRef.current.focus();
    }, 0);
  }, []);

  const handleTriggerClick = useCallback(() => {
    if (disabled) return;
    setOpen((prev) => !prev);
  }, [disabled]);

  // Focus the popover dialog wrapper on mount so Esc keydown bubbles to
  // our handler instead of being eaten inside emoji-mart's shadow root.
  // (Focusing the shadow search input directly worked in dev but Esc
  // there is intercepted by emoji-mart's own search-clear behavior — see
  // smoke notes for KAP-307.) Mirrors `ReactionPicker.js:24-35` in
  // intent, adapted for the composer's shadow-DOM Picker.
  useEffect(() => {
    if (!open) return undefined;
    const focusTimer = window.setTimeout(() => {
      if (popoverRef.current) popoverRef.current.focus();
    }, 0);
    // Capture phase ensures we receive Esc before any handler inside the
    // emoji-mart shadow root can call stopPropagation on it.
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        event.preventDefault();
        closeAndRestoreFocus();
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      window.clearTimeout(focusTimer);
      window.removeEventListener('keydown', onKeyDown, true);
    };
  }, [open, closeAndRestoreFocus]);

  // Outside-click closes (mirrors composer-v2/LinkPopover.js:46-55 and
  // the ReactionBar parent-owned dismissal pattern).
  useEffect(() => {
    if (!open) return undefined;
    const handlePointerDown = (event) => {
      const popover = popoverRef.current;
      const trigger = triggerRef.current;
      if (
        popover &&
        !popover.contains(event.target) &&
        trigger &&
        !trigger.contains(event.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open]);

  const handleEmojiSelect = useCallback(
    (selection) => {
      const glyph = selection && selection.native;
      if (!glyph) return;
      if (typeof onSelectEmoji === 'function') onSelectEmoji(glyph);
      // Picker close only — focus return is the parent's job. The editor
      // takes focus via `EmojiInsertPlugin` after the insertion commits,
      // so the user can keep typing without an extra click. Esc / outside
      // click cancel paths still use `closeAndRestoreFocus` to return
      // focus to the trigger (no insertion happened).
      setOpen(false);
    },
    [onSelectEmoji]
  );

  return (
    <div className="msg-composer-emoji-wrapper">
      <button
        ref={triggerRef}
        type="button"
        className={`msg-composer-emoji-trigger${open ? ' is-open' : ''}`}
        onClick={handleTriggerClick}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label="Insert emoji"
        title="Insert emoji"
      >
        <span aria-hidden="true">😊</span>
      </button>
      {open && (
        <div
          ref={popoverRef}
          className="msg-composer-emoji-popover"
          role="dialog"
          aria-label="Pick an emoji"
          tabIndex={-1}
        >
          <Picker
            data={data}
            onEmojiSelect={handleEmojiSelect}
            previewPosition="none"
            skinTonePosition="none"
            theme="auto"
            navPosition="top"
            perLine={8}
            emojiSize={18}
            maxFrequentRows={1}
          />
        </div>
      )}
    </div>
  );
}

export default ComposerEmojiButton;
